import { useMemo, useState, type ReactNode } from 'react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Card, CardContent } from '@/components/ui/card'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Label } from '@/components/ui/label'
import {
  useClasses,
  useDepartments,
  useHierarchyLevel,
  useSubclasses,
} from '@/hooks/useHierarchy'
import { useSeasons } from '@/hooks/useSeasons'
import { formatDistributionCenterName } from '@/utils/formatters'
import {
  buildSeasonEventDispositionData,
  DC_OPTIONS,
  generateSnapshotOptions,
  IMP_DOM_OPTIONS,
  METRIC_OPTIONS,
  type SeasonEventDispositionFilterState,
  type SeasonEventDispositionMetric,
} from './seasonEventDispositionMockData'
import { SeasonEventDispositionTable } from './SeasonEventDispositionTable'

const ALL = '__all__'

interface HierarchyOption {
  id: string
  code: string
  name: string
}

function FilterField({
  label,
  children,
}: {
  label: string
  children: ReactNode
}) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </label>
      {children}
    </div>
  )
}

function SimpleSelect({
  value,
  onValueChange,
  options,
  loading,
  formatOption = option => option,
}: {
  value: string | null
  onValueChange: (v: string | null) => void
  options: string[]
  loading?: boolean
  formatOption?: (option: string) => string
}) {
  return (
    <Select
      value={value ?? ALL}
      onValueChange={v => onValueChange(v === ALL ? null : v)}
    >
      <SelectTrigger className="w-full bg-white">
        <SelectValue placeholder={loading ? 'Loading...' : 'All'} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL}>All</SelectItem>
        {options.map(o => (
          <SelectItem key={o} value={o}>
            {formatOption(o)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

function HierarchySelect({
  value,
  onValueChange,
  options,
  loading,
  disabled,
}: {
  value: string | null
  onValueChange: (v: string | null) => void
  options: HierarchyOption[]
  loading?: boolean
  disabled?: boolean
}) {
  return (
    <Select
      value={value ?? ALL}
      onValueChange={v => onValueChange(v === ALL ? null : v)}
      disabled={disabled}
    >
      <SelectTrigger className="w-full bg-white">
        <SelectValue placeholder={loading ? 'Loading...' : 'All'} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL}>All</SelectItem>
        {options.map(o => (
          <SelectItem key={o.id} value={o.id}>
            {o.code} - {o.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

/** Plain select with no "All" sentinel — used for Snapshot Base / Compare To,
 * which always resolve to a concrete snapshot id, never an "all" concept. */
function SnapshotSelect({
  value,
  onValueChange,
  options,
}: {
  value: string
  onValueChange: (v: string) => void
  options: string[]
}) {
  return (
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger className="w-full bg-white">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {options.map(o => (
          <SelectItem key={o} value={o}>
            {o}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

function formatTimestamp(date: Date): string {
  return date.toLocaleString('en-US', {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  })
}

const GLOSSARY_ENTRIES: Array<{ term: string; description: string }> = [
  {
    term: 'PO Cost $',
    description:
      'total PO value (USD) currently in each supply-chain stage for the selected event and week.',
  },
  {
    term: 'PO Cost % of Total',
    description: 'share of total PO value in that stage.',
  },
  {
    term: 'Compare To',
    description: 'the same metrics for the selected Compare To snapshot.',
  },
  {
    term: 'Base vs Compare To %',
    description:
      'percent change from the Compare To snapshot to the Base snapshot ((Base - Compare To) / Compare To).',
  },
]

export function SeasonEventDispositionMockup() {
  const [season, setSeason] = useState<string | null>(null)
  const [divisionId, setDivisionId] = useState<string | null>(null)
  const [departmentId, setDepartmentId] = useState<string | null>(null)
  const [classId, setClassId] = useState<string | null>(null)
  const [subclassId, setSubclassId] = useState<string | null>(null)
  const [impDom, setImpDom] = useState<string | null>(null)
  const [dc, setDc] = useState<string | null>(null)
  const [metric, setMetric] =
    useState<SeasonEventDispositionMetric>('Sellable Units')

  const now = useMemo(() => new Date(), [])
  const snapshotOptions = useMemo(
    () => ['Latest', ...generateSnapshotOptions(now, 12)],
    [now]
  )
  const [snapshotBase, setSnapshotBase] = useState<string>('Latest')
  const [compareTo, setCompareTo] = useState<string>(
    snapshotOptions[4] ?? snapshotOptions[snapshotOptions.length - 1]
  )

  const lastUpdate = useMemo(() => formatTimestamp(new Date()), [])

  const { data: seasons } = useSeasons()
  const { data: divisions, isLoading: divisionsLoading } =
    useHierarchyLevel('DIVISION')
  const { data: departments, isLoading: departmentsLoading } = useDepartments()
  const { data: classes, isLoading: classesLoading } = useClasses(departmentId)
  const { data: subclasses, isLoading: subclassesLoading } =
    useSubclasses(classId)

  const seasonOptions = (seasons ?? []).map(s => s.name)
  const divisionOptions: HierarchyOption[] = divisions ?? []
  const departmentOptions: HierarchyOption[] = departments ?? []
  const classOptions: HierarchyOption[] = classes ?? []
  const subclassOptions: HierarchyOption[] = subclasses ?? []

  const handleDivisionChange = (v: string | null) => {
    setDivisionId(v)
    // Division cannot truly cascade (the hierarchy API ignores parentId for
    // DIVISION/DEPARTMENT), but reset downstream selections anyway so a
    // stale Dept/Class/Subclass never visually contradicts the chosen
    // Division.
    setDepartmentId(null)
    setClassId(null)
    setSubclassId(null)
  }

  const handleDepartmentChange = (v: string | null) => {
    setDepartmentId(v)
    setClassId(null)
    setSubclassId(null)
  }

  const handleClassChange = (v: string | null) => {
    setClassId(v)
    setSubclassId(null)
  }

  const filters = useMemo<SeasonEventDispositionFilterState>(
    () => ({
      season,
      division: divisionId,
      departmentId,
      classId,
      subclassId,
      impDom,
      dc,
      snapshotBase,
      metric,
    }),
    [
      season,
      divisionId,
      departmentId,
      classId,
      subclassId,
      impDom,
      dc,
      snapshotBase,
      metric,
    ]
  )

  const data = useMemo(
    () => buildSeasonEventDispositionData(filters),
    [filters]
  )

  return (
    <div className="flex flex-col gap-4 bg-[#fafafa] p-6">
      <div className="flex gap-6">
        {/* LEFT column */}
        <div className="flex flex-1 flex-col gap-4">
          {/* Filter row 1 */}
          <div className="flex items-start justify-between gap-4">
            <div className="grid grid-cols-4 gap-4">
              <FilterField label="Snapshot Base">
                <SnapshotSelect
                  value={snapshotBase}
                  onValueChange={setSnapshotBase}
                  options={snapshotOptions}
                />
              </FilterField>
              <FilterField label="Compare To">
                <SnapshotSelect
                  value={compareTo}
                  onValueChange={setCompareTo}
                  options={snapshotOptions}
                />
              </FilterField>
              <FilterField label="Imp/Dom">
                <SimpleSelect
                  value={impDom}
                  onValueChange={setImpDom}
                  options={IMP_DOM_OPTIONS}
                />
              </FilterField>
              <FilterField label="DC">
                <SimpleSelect
                  value={dc}
                  onValueChange={setDc}
                  options={DC_OPTIONS}
                  formatOption={formatDistributionCenterName}
                />
              </FilterField>
            </div>
          </div>

          {/* Filter row 2 */}
          <div className="grid grid-cols-5 gap-4">
            <FilterField label="Season">
              <SimpleSelect
                value={season}
                onValueChange={setSeason}
                options={seasonOptions}
              />
            </FilterField>
            <FilterField label="Division">
              <HierarchySelect
                value={divisionId}
                onValueChange={handleDivisionChange}
                options={divisionOptions}
                loading={divisionsLoading}
              />
            </FilterField>
            <FilterField label="Department">
              <HierarchySelect
                value={departmentId}
                onValueChange={handleDepartmentChange}
                options={departmentOptions}
                loading={departmentsLoading}
              />
            </FilterField>
            <FilterField label="Class">
              <HierarchySelect
                value={classId}
                onValueChange={handleClassChange}
                options={classOptions}
                loading={classesLoading}
                disabled={!departmentId}
              />
            </FilterField>
            <FilterField label="SubClass">
              <HierarchySelect
                value={subclassId}
                onValueChange={setSubclassId}
                options={subclassOptions}
                loading={subclassesLoading}
                disabled={!classId}
              />
            </FilterField>
          </div>

          {/* Season readback + What You Need to Know */}
          <div className="flex gap-4">
            <Card className="w-56 shrink-0">
              <CardContent className="p-4">
                <p className="mb-2 text-sm font-semibold text-foreground">
                  Season
                </p>
                <div className="rounded-md border border-border bg-muted/30 px-3 py-2 text-sm text-foreground">
                  {season ?? 'All Seasons'}
                </div>
              </CardContent>
            </Card>
            <Card className="flex-1">
              <CardContent className="p-4">
                <p className="mb-2 text-sm font-semibold text-foreground">
                  What You Need to Know
                </p>
                <div className="rounded-md border border-border px-3 py-2 text-sm text-foreground">
                  {data.whatYouNeedToKnow}
                </div>
              </CardContent>
            </Card>
          </div>

          <SeasonEventDispositionTable data={data} />
        </div>

        {/* RIGHT rail */}
        <div className="flex w-64 shrink-0 flex-col gap-4 text-[11px] text-muted-foreground">
          <p className="leading-tight">
            Current supply chain and sales status for each seasonal event,
            calculated using {metric.toLowerCase()}.
          </p>

          <div className="text-right">
            <div>Last Updated</div>
            <div>{lastUpdate}</div>
          </div>

          <div className="flex flex-col gap-2">
            {GLOSSARY_ENTRIES.map(entry => (
              <p key={entry.term} className="leading-tight">
                <span className="font-semibold text-foreground">
                  {entry.term}:
                </span>{' '}
                {entry.description}
              </p>
            ))}
          </div>

          <Card>
            <CardContent className="p-4">
              <p className="mb-2 text-sm font-semibold text-foreground">
                Metric
              </p>
              <RadioGroup
                value={metric}
                onValueChange={v =>
                  setMetric(v as SeasonEventDispositionMetric)
                }
              >
                {METRIC_OPTIONS.map(option => (
                  <div key={option} className="flex items-center gap-2">
                    <RadioGroupItem
                      value={option}
                      id={`metric-${option}`}
                      className="cursor-pointer"
                    />
                    <Label
                      htmlFor={`metric-${option}`}
                      className="cursor-pointer text-sm font-normal text-foreground"
                    >
                      {option}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
